import { MockList } from '@graphql-tools/mock';
import Chance from 'chance';
import { v4 as createUuid } from 'uuid';

import availabilityOptions from '../spec/AvailabilityOptions';
import Classification from '../spec/Classification';
import Credential from '../spec/Credential';
import currentLocation from '../spec/CurrentLocation';
import currentLocation2 from '../spec/CurrentLocation2';
import GenerateResumeFromProfilePayload from '../spec/GenerateResumeFromProfilePayload';
import languageProficiencies from '../spec/LanguageProficiencies';
import Licence from '../spec/Licence';
import nextRolePreferredLocations from '../spec/NextRolePreferredLocations';
import NoticePeriod from '../spec/NoticePeriod';
import personalDetails from '../spec/PersonalDetails';
import preferredClassificationOptions from '../spec/PreferredClassificationOptions';
import preferredLocations2 from '../spec/PreferredLocations2';
import ProcessUploadedResumeResult from '../spec/ProcessUploadedResumeResult';
import ProfileScore from '../spec/ProfileScore';
import Qualification from '../spec/Qualification';
import RecommendedJob from '../spec/RecommendedJob';
import Resume from '../spec/Resume';
import rightsToWork from '../spec/RightToWork';
import rightToWorkOptions from '../spec/RightToWorkOptions';
import Role from '../spec/Role';
import Salary from '../spec/Salary';
import salaryOptions from '../spec/SalaryOptions';
import SalaryPreference2 from '../spec/SalaryPreference2';
import SavedSearch from '../spec/SavedSearch';
import Skill from '../spec/Skill';
import summary from '../spec/Summary';
import supportedCountries from '../spec/SupportedCountries';
import UnconfirmedData from '../spec/UnconfirmedData';
import UnconfirmedDataCompleted from '../spec/UnconfirmedDataCompleted';
import UnconfirmedDataPending from '../spec/UnconfirmedDataPending';
import workTypeOptions from '../spec/WorkTypeOptions';
import yearsOfExperience from '../spec/YearsOfExperience';
import KeywordSuggestion from '../spec/mockResolvers/searchKeywordSuggestions/createKeywordSuggestion';
import {
  OntologyCountry as IOntologyCountry,
  OntologyEducationProvider as IOntologyEducationProvider,
  OntologyOrganisation as IOntologyOrganisation,
  OntologyQualification as IOntologyQualification,
  OntologyRoleTitleSeniority as IOntologyRoleTitleSeniority,
  SuggestionHighlight,
} from '../spec/ontologySuggestions';
import SearchLocationSuggestions from '../spec/searchLocationSuggestions/SearchLocationSuggestions';
import LocationSuggestion from '../spec/searchLocationSuggestions/createLocationSuggestion';
import {
  languageProficiencies as languageProficienciesShareableProfile,
  personalDetails as personalDetailsShareableProfile,
  summary as summaryShareableProfile,
} from '../test/shareableProfileTestData';
import {
  profileInsightsWithActivities,
  profileInsightsWithoutActivities,
  profileInsightsWithoutSearchImpressions,
  profileSuggestionsWithData,
  shareableProfileVerifiedIdentities,
  verificationsComplete,
  verificationsEmpty,
  verificationsEmptyVerified,
} from '../test/test-data';

const randomInt1To10 = () => Math.floor(Math.random() * 10) + 1;

const profileSettings = [
  {
    active: true,
    profileSlug: 'fred-smith-abcd1234',
    url: 'https://www.seek.com.au/profile/fred-smith-abcd1234',
    displayUrl: 'seek.com.au/profile/fred-smith-abcd1234',
    views: 5,
  },
  {
    active: null,
    profileSlug: null,
    url: null,
    views: null,
  },
  {
    active: false,
    profileSlug: 'fred-smith-abcd1234',
    url: 'https://www.seek.com.au/profile/fred-smith-abcd1234',
    displayUrl: 'seek.com.au/profile/fred-smith-abcd1234',
    views: 5,
  },
];

const shareableProfileSettings = () => {
  const zeroToTwo = randomInt1To10() % 3;
  return profileSettings[zeroToTwo];
};

const shareableProfileVerifiedIdentity = () => {
  const zeroToTwo = randomInt1To10() % 3;
  return shareableProfileVerifiedIdentities[zeroToTwo];
};

const profileInsights = () => {
  const zeroToTwo = randomInt1To10() % 3;
  const profileInsightsPossibleData = [
    null,
    profileInsightsWithoutActivities,
    profileInsightsWithActivities,
    profileInsightsWithoutSearchImpressions,
  ];
  return profileInsightsPossibleData[zeroToTwo];
};

const profileSuggestions = () => profileSuggestionsWithData;

const profileVerifications = () => {
  const zeroToThree = randomInt1To10() % 4;
  const verificationsPossibleData = [
    null,
    verificationsComplete,
    verificationsEmpty,
    verificationsEmptyVerified,
  ];
  return verificationsPossibleData[zeroToThree];
};

const chance = new Chance();

const ID = chance.natural({ min: 10000000, max: 99999999 }).toString();
export const mockOverrides = {
  DocumentDownloadLink: {
    link: 'http://localhost:3000/profile/resumes/download/1234',
  },

  Candidate: () => ({
    personalDetails: () => personalDetails(),
    currentLocation: () => currentLocation(),
    currentLocation2: () => currentLocation2(),
    emailAddress: () => chance.email(),
    minimumProfile: () => chance.bool(),
    qualifications: () => new MockList([0, 10]),
    roles: () => new MockList([0, 4]),
    savedSearches: () => new MockList([0, 10]),
    skills: () => new MockList([0, 20]),
    careerObjectives: () => summary(),
    nextRolePreferredLocations: () => nextRolePreferredLocations(),
    preferredLocations2: () => preferredLocations2(),
    resumes: () => new MockList([0, 10]),
    rightsToWork: () => rightsToWork(),
    licences: () => new MockList([0, 3]),
    languageProficiencies: () => languageProficiencies(),
    yearsOfExperience: () => yearsOfExperience(),
    salaryPreferences2: () => new MockList([0, 4]),
    shareableProfileSettings: () => shareableProfileSettings(),
    profileInsights: () => profileInsights(),
    profileSuggestions: () => profileSuggestions(),
    profileVerifications: () => profileVerifications(),
  }),
  Qualification,
  Role,
  Classification,
  Credential,
  GenerateResumeFromProfilePayload,
  LocationSuggestion,
  KeywordSuggestion,
  NoticePeriod,
  IOntologyCountry,
  IOntologyEducationProvider,
  IOntologyOrganisation,
  IOntologyQualification,
  IOntologyRoleTitleSeniority,
  Licence,
  ProcessUploadedResumeResult,
  ProfileScore,
  RecommendedJob,
  Resume,
  Salary,
  SalaryPreference2,
  SavedSearch,
  SearchLocationSuggestions,
  Skill,
  SuggestionHighlight,
  UnconfirmedDataPending,
  UnconfirmedDataCompleted,
  UnconfirmedData,
  Query: () => ({
    ontologyEducationProviders: () => new MockList([10, 10]),
    ontologyOrganisations: () => new MockList([10, 10]),
    ontologyRoleTitlesSeniority: () => new MockList([10, 10]),
    ontologyQualifications: () => new MockList([10, 10]),
    salaryOptions,
    rightToWorkOptions,
    availabilityOptions,
    workTypeOptions,
    preferredClassificationOptions,
    supportedCountries,
  }),
  Int: () => chance.natural({ min: 1, max: 2147483647 }),
  ID: () => ID,
  Email: () => chance.email(),
  UUID: () => createUuid(),
  DateTime: () => chance.date(),
  ShareableProfile: () => ({
    active: randomInt1To10() >= 2,
    // Only need custom mocks for these ones as they resolve to field names rather than types
    personalDetails: personalDetailsShareableProfile(),
    summary: summaryShareableProfile(),
    languageProficiencies: languageProficienciesShareableProfile(),
    verifiedIdentity: shareableProfileVerifiedIdentity(),
  }),
};
